import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import Select from "react-select";
import Axios from "axios";
import { DEV } from "../../../services/constants";
import { useDebounce } from "../../../custom hooks/Debounce";
import { handleGetRequest } from "../../../services/GetTemplate";
import styles from "../packages.module.scss";

// ─── CSV Helper ───

const escapeCSVValue = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

const generateCSV = (columns, rows) => {
    const header = columns.map((col) => escapeCSVValue(col.header)).join(",");
    const body = rows.map((row) => columns.map((col) => escapeCSVValue(row[col.dataKey])).join(","));
    return [header, ...body].join("\n");
};

const downloadCSV = (csvString, fileName) => {
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// ─── Component ───

function PackagesTable({
    searchedBrands,
    setselectedRow,
    selectedRow,
    loading,
    role,
    setSearchedBrands,
    setShipmentNumber,
    brands,
    userId,
    setUploadShow,
    setPackage,
    setLoading,
    handelDel,
    setPackageDetail,
    values,
    setValues,
    // Pagination props from parent
    currentPage,
    totalPages,
    totalRecords,
    limit,
    onPageChange,
    // ✅ NEW: callback so search can push pagination metadata back to parent
    onSearchPaginationChange,
}) {
    const [filters, setFilters] = useState({});
    // ✅ "Bill To" filter options, fetched once from the backend so it can be a searchable dropdown
    const [billToOptions, setBillToOptions] = useState([]);
    // ✅ Fields that have no backend search support — filtered client-side over
    // the currently loaded page, same as the column filters they replace.
    const [localFilters, setLocalFilters] = useState({ consigneeType: "", description: "" });
    const [csvLoading, setCsvLoading] = useState(false);
    const [selectAllLoading, setSelectAllLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // ✅ Track whether we are in "search mode" (any filter is active)
    const isSearchMode = Object.values(filters).some((v) => v && v.trim() !== "");

    // ✅ Local pagination state used only during search mode
    const [searchPage, setSearchPage] = useState(1);
    const [searchTotalPages, setSearchTotalPages] = useState(0);
    const [searchTotalRecords, setSearchTotalRecords] = useState(0);

    const ITEMS_PER_PAGE = 100;

    const isFirstRender = useRef(true);
    // ✅ Keep latest filters accessible inside page-change effect without re-running it
    const filtersRef = useRef(filters);
    const toast = useRef(null);
    const history = useHistory();

    const debouncedFilters = useDebounce(filters, 500);

    // Keep filtersRef in sync
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    // ✅ Fetch users for the "Bill To" filter dropdown
    useEffect(() => {
        if (role === "client") return;
        handleGetRequest("/clientAndAdmins")
            .then((res) => {
                const options = (res?.data || []).map((user) => ({
                    value: user._id,
                    label: `${user.firstName} ${user.lastName}`,
                }));
                setBillToOptions(options);
            })
            .catch(() => setBillToOptions([]));
    }, [role]);

    // ─── Unified Search ───

    const executeSearch = useCallback(
        async (searchFilters, page = 1) => {
            try {
                setLoading(true);

                if (searchFilters.flightNumber) {
                    setShipmentNumber(searchFilters.flightNumber);
                }

                const result = await Axios.get(`${DEV}/searchPackage`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    params: { ...searchFilters, page, limit: ITEMS_PER_PAGE },
                });

                const responseData = result?.data?.data;
                let filteredData = responseData?.data || [];

                // Client-side role filtering (populated fields not queryable server-side)
                if (role === "client" || role === "reseller" || role === "corporate") {
                    filteredData = filteredData.filter((val) => val?.name?.client?.id === userId);
                } else if (role === "courier") {
                    filteredData = filteredData.filter((val) => val?.courierName?._id === userId);
                }

                setSearchedBrands(filteredData);

                // ✅ Update search-mode pagination metadata from the response
                const serverTotalRecords = responseData?.totalRecords ?? 0;
                const serverTotalPages = responseData?.totalPages ?? 0;
                const serverPage = responseData?.page ?? page;

                setSearchTotalRecords(serverTotalRecords);
                setSearchTotalPages(serverTotalPages);
                setSearchPage(serverPage);

                // ✅ Bubble pagination metadata up to parent so its display stays consistent
                if (onSearchPaginationChange) {
                    onSearchPaginationChange({
                        currentPage: serverPage,
                        totalPages: serverTotalPages,
                        totalRecords: serverTotalRecords,
                    });
                }
            } catch (error) {
                console.error("Search failed:", error);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Search Error",
                        detail: "Failed to search packages",
                        life: 3000,
                    });
                }
            } finally {
                setLoading(false);
            }
        },
        [role, userId, setLoading, setSearchedBrands, setShipmentNumber, onSearchPaginationChange],
    );

    // ─── Effect: Re-run search when debounced filters change ───
    // Always resets to page 1 when a filter value changes.

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // If all filters cleared, reset search pagination state
        const hasActiveFilter = Object.values(debouncedFilters).some((v) => v && v.trim() !== "");
        if (!hasActiveFilter) {
            setSearchPage(1);
            setSearchTotalPages(0);
            setSearchTotalRecords(0);
        }

        executeSearch(debouncedFilters, 1);
    }, [debouncedFilters]); // eslint-disable-line react-hooks/exhaustive-deps
    // ☝️ executeSearch intentionally omitted: we only want this to fire on filter changes.
    //    Including executeSearch would cause double-fetches on every keystroke.

    // ─── Effect: Re-run search when parent-driven page changes ───
    // Only active in search mode so normal (non-search) pagination is unaffected.

    useEffect(() => {
        if (isFirstRender.current) return;
        if (!isSearchMode) return;

        // currentPage here is the value the parent set via onPageChange (from pagination buttons)
        executeSearch(filtersRef.current, currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);
    // ☝️ Only re-run when the page number changes; filters are read via ref to avoid
    //    retriggering this effect when filter state updates simultaneously.

    const updateFilter = useCallback(
        (fieldName, value) => {
            setFilters((prev) => ({ ...prev, [fieldName]: value }));
            setLoading(true);
        },
        [setLoading],
    );

    const clearAllFilters = useCallback(() => {
        setFilters({});
        setLocalFilters({ consigneeType: "", description: "" });
        setValues({});
        setSearchPage(1);
        setSearchTotalPages(0);
        setSearchTotalRecords(0);
        executeSearch({}, 1);
    }, [executeSearch, setValues]);

    const hasAnyFilter = Object.keys(filters).length > 0 || !!localFilters.consigneeType || !!localFilters.description;
    const activeFilterCount = Object.values(filters).filter((v) => v && v.trim() !== "").length + (localFilters.consigneeType ? 1 : 0) + (localFilters.description ? 1 : 0);

    // ─── Client-side filtering for fields with no backend search support ───
    // Mirrors the previous default PrimeReact row-filter behavior for these
    // columns (substring match against the currently loaded page).

    const displayedBrands = useMemo(() => {
        let data = searchedBrands;
        if (localFilters.consigneeType) {
            const needle = localFilters.consigneeType.toLowerCase();
            data = data.filter((row) => (row?.consigneeType || "").toLowerCase().includes(needle));
        }
        if (localFilters.description) {
            const needle = localFilters.description.toLowerCase();
            data = data.filter((row) => (row?.description || "").toLowerCase().includes(needle));
        }
        return data;
    }, [searchedBrands, localFilters]);

    // ─── Derive which pagination values to display ───
    // In search mode use local search pagination; otherwise use parent-provided values.

    const activePage = isSearchMode ? searchPage : currentPage;
    const activeTotal = isSearchMode ? searchTotalPages : totalPages;
    const activeRecords = isSearchMode ? searchTotalRecords : totalRecords;

    // ─── Select All Across Pages ───
    // The DataTable's built-in checkbox column only selects rows currently
    // loaded in `value` (the active page). This fetches every record matching
    // the current filters (or every record, if unfiltered) in one request so
    // bulk actions can act on all of them at once, not just the visible page.

    const isAllSelected = activeRecords > 0 && selectedRow?.length === activeRecords;

    const handleSelectAllFiltered = useCallback(async () => {
        if (isAllSelected) {
            setselectedRow([]);
            return;
        }

        try {
            setSelectAllLoading(true);

            let allRecords = [];

            if (isSearchMode) {
                const result = await Axios.get(`${DEV}/searchPackage`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    params: { ...filtersRef.current, page: 1, limit: activeRecords || 100000 },
                });
                allRecords = result?.data?.data?.data || [];

                if (role === "client" || role === "reseller" || role === "corporate") {
                    allRecords = allRecords.filter((val) => val?.name?.client?.id === userId);
                } else if (role === "courier") {
                    allRecords = allRecords.filter((val) => val?.courierName?._id === userId);
                }
            } else {
                let res;
                if (role === "admin" || role === "handler" || role === "clerk" || role === "driver") {
                    res = await handleGetRequest(`/allPackagesForTable?page=1&limit=${activeRecords || 100000}`);
                } else if (role === "courier") {
                    res = await handleGetRequest(`/courierpackagesForTable?userid=${userId}&page=1&limit=${activeRecords || 100000}`);
                } else if (role === "client" || role === "reseller" || role === "corporate") {
                    res = await handleGetRequest(`/clientpackagesForTable?userid=${userId}&page=1&limit=${activeRecords || 100000}`);
                }
                allRecords = res?.data?.data || [];
            }

            setselectedRow(allRecords);

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Selected",
                    detail: `${allRecords.length} package${allRecords.length === 1 ? "" : "s"} selected`,
                    life: 2500,
                });
            }
        } catch (error) {
            console.error("Select all failed:", error);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Select All Failed",
                    detail: "Could not fetch all packages",
                    life: 3000,
                });
            }
        } finally {
            setSelectAllLoading(false);
        }
    }, [isAllSelected, isSearchMode, activeRecords, role, userId, setselectedRow]);

    // ─── CSV Export ───

    const handleExportCSV = useCallback(() => {
        try {
            setCsvLoading(true);

            const dataToExport = selectedRow && selectedRow.length > 0 ? selectedRow : searchedBrands;

            if (!dataToExport || dataToExport.length === 0) {
                if (toast.current) {
                    toast.current.show({
                        severity: "warn",
                        summary: "No Data",
                        detail: "There are no records to export",
                        life: 3000,
                    });
                }
                setCsvLoading(false);
                return;
            }

            const columns = [
                { header: "Waybill", dataKey: "Waybill" },
                { header: "Package Name", dataKey: "billTo" },
            ];

            if (role !== "client") {
                columns.push({ header: "Bill To", dataKey: "_name" });
            }
            if (role === "admin") {
                columns.push({ header: "Consignee Name", dataKey: "consigneeName" });
                columns.push({ header: "Client Type", dataKey: "consigneeType" });
            }

            columns.push({ header: "Shipment Number", dataKey: "_shipment" });

            if (role !== "client") {
                columns.push({ header: "Courier Name", dataKey: "_courier" });
                columns.push({ header: "Description", dataKey: "description" });
            }
            if (role === "admin") {
                columns.push({ header: "Merchant", dataKey: "merchant" });
            }

            columns.push({ header: "Tracking Number", dataKey: "trackingNumber" }, { header: "Delivery Information", dataKey: "deliveryInfo" }, { header: "Package Status", dataKey: "packageStatus" }, { header: "Weight", dataKey: "_weight" }, { header: "Created On", dataKey: "date" });

            const rows = dataToExport.map((pkg) => ({
                Waybill: pkg.Waybill || "",
                billTo: pkg.billTo || "",
                _name: `${pkg.name?.firstName || ""} ${pkg.name?.lastName || ""}`.trim(),
                consigneeName: pkg.consigneeName || "",
                consigneeType: pkg.consigneeType || "",
                _shipment: pkg.shipmentMethod === "air" ? pkg.flightNumber || "" : pkg.shipmentNumber || "",
                _courier: pkg.courierName?.courier?.businessName || "",
                description: pkg.description || "",
                merchant: pkg.merchant || "",
                trackingNumber: pkg.trackingNumber || "",
                deliveryInfo: pkg.deliveryInfo || "",
                packageStatus: pkg.packageStatus || "",
                _weight: pkg.weight ? `${pkg.weight.number} ${pkg.weight.unit}` : "",
                date: pkg.date || "",
            }));

            const csvString = generateCSV(columns, rows);
            const fileName = `packages-${new Date().toISOString().slice(0, 10)}.csv`;
            downloadCSV(csvString, fileName);

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Export Complete",
                    detail: `${fileName} downloaded with ${rows.length} records`,
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("CSV export failed:", error);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Export Error",
                    detail: "Failed to generate CSV",
                    life: 3000,
                });
            }
        } finally {
            setCsvLoading(false);
        }
    }, [selectedRow, searchedBrands, role]);

    // ─── Column Templates ───

    const nameTemplate = (rowdata) => (
        <span>
            {rowdata?.name?.firstName} {rowdata?.name?.lastName}
        </span>
    );

    const courierNameTemplate = (rowdata) => <span>{rowdata?.courierName?.courier?.businessName}</span>;

    const weightTemplate = (rowdata) => (
        <span>
            {rowdata?.weight?.number} {rowdata?.weight?.unit}
        </span>
    );

    const shipmentTemplate = (rowdata) => <span>{rowdata?.shipmentMethod === "air" ? rowdata?.flightNumber : rowdata?.shipmentNumber}</span>;

    // ─── Filter Field (rendered in the standalone Filters panel) ───

    const renderFilterField = (label, fieldName, { local = false } = {}) => {
        const value = local ? localFilters[fieldName] || "" : filters[fieldName] || "";
        const onChange = (e) => {
            if (local) {
                setLocalFilters((prev) => ({ ...prev, [fieldName]: e.target.value }));
            } else {
                updateFilter(fieldName, e.target.value);
            }
        };
        return (
            <div className={styles.filterField} key={fieldName}>
                <label>{label}</label>
                <InputText value={value} onChange={onChange} placeholder="Search..." className={styles.filterInput} />
            </div>
        );
    };

    // ─── Bill To Filter (searchable dropdown of users, rendered in the standalone Filters panel) ───

    const renderBillToFilterField = () => {
        const selected = billToOptions.find((opt) => opt.value === filters.name) || null;
        return (
            <div className={styles.filterField} key="name">
                <label>Bill To</label>
                <Select value={selected} options={billToOptions} onChange={(selectedOption) => updateFilter("name", selectedOption?.value || "")} isClearable placeholder="Search..." />
            </div>
        );
    };

    // ─── Invoiced Filter (rendered in the standalone Filters panel) ───

    const renderInvoicedFilterField = () => (
        <div className={styles.filterField} key="invoiced">
            <label>Invoiced</label>
            <select className={styles.filterInput} value={filters.invoiced || ""} onChange={(e) => updateFilter("invoiced", e.target.value)}>
                <option value="">All</option>
                <option value="true">Invoiced</option>
                <option value="false">Not Invoiced</option>
            </select>
        </div>
    );

    // ─── Action Buttons ───

    const handleActionButton = (e, rowdata) => {
        e.preventDefault();
        history.push(`package/${rowdata?.Waybill}`);
    };

    // ─── Card Selection ───
    // Identity by Waybill (rather than object reference) so a card's checkbox
    // stays in sync with selectedRow even after "Select All" repopulates it
    // from a separate fetch.

    const isSelected = useCallback((pkg) => !!selectedRow?.some((r) => r?.Waybill === pkg?.Waybill), [selectedRow]);

    const toggleSelect = useCallback(
        (pkg) => {
            setselectedRow((prev) => {
                const exists = prev?.some((r) => r?.Waybill === pkg?.Waybill);
                if (exists) return prev.filter((r) => r?.Waybill !== pkg?.Waybill);
                return [...(prev || []), pkg];
            });
        },
        [setselectedRow],
    );

    // ─── Card Expand/Collapse ───
    // Collapsed cards show just the at-a-glance fields; expanding reveals the rest.

    const [expandedCards, setExpandedCards] = useState(() => new Set());

    const toggleExpand = useCallback((waybill) => {
        setExpandedCards((prev) => {
            const next = new Set(prev);
            if (next.has(waybill)) next.delete(waybill);
            else next.add(waybill);
            return next;
        });
    }, []);

    // ─── Sorting (client-side, over the currently loaded page) ───

    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState(1);

    const sortOptions = [
        { label: "Waybill", value: "Waybill" },
        { label: "Package Name", value: "billTo" },
        { label: "Consignee Name", value: "consigneeName" },
        { label: "Created On", value: "date" },
    ];

    const sortedBrands = useMemo(() => {
        if (!sortField) return displayedBrands;
        return [...displayedBrands].sort((a, b) => {
            const av = (a?.[sortField] ?? "").toString().toLowerCase();
            const bv = (b?.[sortField] ?? "").toString().toLowerCase();
            if (av < bv) return -1 * sortOrder;
            if (av > bv) return 1 * sortOrder;
            return 0;
        });
    }, [displayedBrands, sortField, sortOrder]);

    // ─── Pagination Handlers ───
    // In search mode: update local searchPage + call onPageChange so the parent
    // tracks the page too (which triggers the currentPage effect above).
    // In normal mode: delegate entirely to the parent.

    const startRecord = activeRecords > 0 ? (activePage - 1) * ITEMS_PER_PAGE + 1 : 0;
    const endRecord = Math.min(activePage * ITEMS_PER_PAGE, activeRecords);

    const handlePageChange = useCallback(
        (newPage) => {
            if (isSearchMode) {
                setSearchPage(newPage);
                // Also notify parent so currentPage prop stays in sync
                onPageChange(newPage);
            } else {
                onPageChange(newPage);
            }
        },
        [isSearchMode, onPageChange],
    );

    const handleFirstPage = () => {
        if (activePage > 1) handlePageChange(1);
    };

    const handlePreviousPage = () => {
        if (activePage > 1) handlePageChange(activePage - 1);
    };

    const handleNextPage = () => {
        if (activePage < activeTotal) handlePageChange(activePage + 1);
    };

    const handleLastPage = () => {
        if (activePage < activeTotal) handlePageChange(activeTotal);
    };

    // ─── Custom Pagination Component ───

    const CustomPagination = () => (
        <div className={styles.paginationBar}>
            <div className={styles.paginationNav}>
                <Button icon="pi pi-step-backward" className={`p-button-rounded p-button-text ${styles.paginationIconBtn}`} onClick={handleFirstPage} disabled={activePage === 1 || activeTotal === 0} tooltip="First Page" />
                <Button icon="pi pi-chevron-left" className={`p-button-rounded p-button-text ${styles.paginationIconBtn}`} onClick={handlePreviousPage} disabled={activePage === 1 || activeTotal === 0} tooltip="Previous Page" />
            </div>

            <div className={styles.paginationDivider} />

            <div className={styles.paginationInfo}>
                <span className={styles.paginationPill}>
                    Page {activePage} of {activeTotal}
                </span>
                {activeRecords > 0 && (
                    <span className={styles.paginationRange}>
                        {startRecord}–{endRecord} of {activeRecords}
                    </span>
                )}
            </div>

            <div className={styles.paginationDivider} />

            <div className={styles.paginationNav}>
                <Button icon="pi pi-chevron-right" className={`p-button-rounded p-button-text ${styles.paginationIconBtn}`} onClick={handleNextPage} disabled={activePage === activeTotal || activeTotal === 0} tooltip="Next Page" />
                <Button icon="pi pi-step-forward" className={`p-button-rounded p-button-text ${styles.paginationIconBtn}`} onClick={handleLastPage} disabled={activePage === activeTotal || activeTotal === 0} tooltip="Last Page" />
            </div>
        </div>
    );

    // ─── Table Header ───

    const header = (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <span style={{ fontWeight: 600, fontSize: "1.1rem" }}>
                    Packages
                    {activeRecords > 0 && (
                        <span style={{ fontWeight: 400, color: "#888", marginLeft: 8 }}>
                            ({activeRecords} total{isSearchMode ? " — filtered" : ""})
                        </span>
                    )}
                </span>
            </div>

            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {selectedRow?.length > 0 && <span style={{ fontWeight: 600, fontSize: "0.9rem", color: "#428bca" }}>{selectedRow.length} selected</span>}
                <Button
                    label={isAllSelected ? "Deselect All" : `Select All${activeRecords > 0 ? ` ${activeRecords}` : ""}`}
                    icon={isAllSelected ? "pi pi-times" : "pi pi-check-square"}
                    className="p-button-outlined p-button-sm"
                    onClick={handleSelectAllFiltered}
                    loading={selectAllLoading}
                    disabled={!activeRecords}
                />
                <Button
                    label={selectedRow?.length > 0 ? `Export ${selectedRow.length} Selected` : "Export CSV"}
                    icon="pi pi-download"
                    className="p-button-sm p-button-success"
                    onClick={handleExportCSV}
                    loading={csvLoading}
                    disabled={(!searchedBrands || searchedBrands.length === 0) && !selectedRow?.length}
                />
            </div>
        </div>
    );

    // ─── Unified Search Bar ───
    // A single box that OR-matches the term across every searchable field on
    // the backend (waybill, names, tracking/shipment numbers, etc.). Lives in
    // the `filters` state under the "search" key so it reuses the same
    // debounce + executeSearch pipeline as the advanced filters below. Sits
    // in the same panel as the advanced filters (mirrors the Invoices page,
    // where the global search box and the advanced-filter toggle are one
    // unit) rather than as its own separate floating card.

    const globalSearchBar = (
        <div className={styles.globalSearchBar}>
            <i className={`pi pi-search ${styles.globalSearchIcon}`} />
            <InputText value={filters.search || ""} onChange={(e) => updateFilter("search", e.target.value)} placeholder="Search all packages by waybill, name, tracking number, description..." className={styles.globalSearchInput} />
            {filters.search && <i className={`pi pi-times ${styles.globalSearchClear}`} onClick={() => updateFilter("search", "")} />}
        </div>
    );

    // ─── Filters Panel (every filter previously embedded in the table's column headers) ───
    // On mobile this same panel detaches into a bottom-sheet (see .filtersCardOpen /
    // .filtersBackdrop media-query rules) so it's reachable via the floating button
    // below without scrolling back to the top of the page.

    const filtersPanel = (
        <div className={`${styles.filtersCard} ${showFilters ? styles.filtersCardOpen : ""}`}>
            <div className={styles.filtersHeaderRow}>
                {globalSearchBar}
                <button type="button" className={styles.filtersToggle} onClick={() => setShowFilters((s) => !s)}>
                    <span className={styles.filtersTitle}>
                        <i className="pi pi-filter" />
                        Filters
                        {activeFilterCount > 0 && <span className={styles.filterCountBadge}>{activeFilterCount}</span>}
                    </span>
                    <i className={`pi ${showFilters ? "pi-chevron-up" : "pi-chevron-down"} ${styles.filtersChevron}`} />
                </button>
            </div>

            {showFilters && (
                <>
                    <div className={styles.filterGrid}>
                        {renderFilterField("Waybill", "Waybill")}
                        {renderFilterField("Package Name", "billTo")}
                        {role !== "client" && renderBillToFilterField()}
                        {(role === "admin" || role === "clerk") && renderFilterField("Consignee Name", "consigneeName")}
                        {(role === "admin" || role === "clerk") && renderFilterField("Client Type", "consigneeType", { local: true })}
                        {renderFilterField("Shipment Number", "flightNumber")}
                        {role !== "client" && renderFilterField("Courier Name", "courierName")}
                        {role !== "client" && renderFilterField("Description", "description", { local: true })}
                        {(role === "admin" || role === "clerk") && renderFilterField("Merchant", "merchant")}
                        {renderFilterField("Tracking Number", "trackingNumber")}
                        {renderFilterField("Delivery Information", "deliveryInfo")}
                        {renderFilterField("Package Status", "packageStatus")}
                        {(role === "admin" || role === "clerk") && renderInvoicedFilterField()}
                        {renderFilterField("Weight", "weight")}
                        {renderFilterField("Created On", "date")}
                    </div>
                    <div className={styles.filtersFooter}>
                        <Button label="Clear Filters" icon="pi pi-filter-slash" className="p-button-outlined p-button-secondary p-button-sm" onClick={clearAllFilters} disabled={!hasAnyFilter} />
                    </div>
                </>
            )}
        </div>
    );

    // Tapping the backdrop closes the mobile filter sheet; invisible/no-op on desktop.
    const filtersBackdrop = <div className={`${styles.filtersBackdrop} ${showFilters ? styles.filtersBackdropVisible : ""}`} onClick={() => setShowFilters(false)} />;

    // Floating trigger so filters stay reachable on mobile without scrolling back up.
    const filtersFab = (
        <button type="button" className={styles.filtersFab} onClick={() => setShowFilters((s) => !s)} aria-label="Toggle filters">
            <i className="pi pi-filter" />
            {activeFilterCount > 0 && <span className={styles.filtersFabBadge}>{activeFilterCount}</span>}
        </button>
    );

    // ─── Package Card ───

    const renderPackageCard = (pkg) => {
        const isExpanded = expandedCards.has(pkg.Waybill);
        const hasNotes = !!pkg.notes;
        const isPaid = !!pkg.isPaid;
        const statusClass = isPaid ? styles.cardPaid : hasNotes ? styles.cardHasNotes : "";

        return (
            <div key={pkg.Waybill} className={`${styles.packageCard} ${isSelected(pkg) ? styles.packageCardSelected : ""} ${statusClass}`}>
                <div className={styles.cardHeader}>
                    <label className={styles.cardCheckbox}>
                        <input type="checkbox" checked={isSelected(pkg)} onChange={() => toggleSelect(pkg)} />
                        <span className={styles.waybill}>#{pkg.Waybill}</span>
                        {pkg.invoiceuploaded && <i className={`pi pi-file-pdf ${styles.invoiceIcon}`} title="Invoice uploaded" />}
                    </label>
                    {pkg.packageStatus && <span className={styles.statusBadge}>{pkg.packageStatus}</span>}
                </div>

                <div className={styles.cardBody}>
                    <div className={styles.infoRow}>
                        <label>Package Name</label>
                        <span>{pkg.billTo}</span>
                    </div>
                    {role !== "client" && (
                        <div className={styles.infoRow}>
                            <label>Bill To</label>
                            {nameTemplate(pkg)}
                        </div>
                    )}
                    <div className={styles.infoRow}>
                        <label>Shipment Number</label>
                        {shipmentTemplate(pkg)}
                    </div>
                    <div className={styles.infoRow}>
                        <label>Weight</label>
                        {weightTemplate(pkg)}
                    </div>

                    {isExpanded && (
                        <>
                            {(role === "admin" || role === "clerk") && (
                                <div className={styles.infoRow}>
                                    <label>Consignee Name</label>
                                    <span>{pkg.consigneeName}</span>
                                </div>
                            )}
                            {(role === "admin" || role === "clerk") && (
                                <div className={styles.infoRow}>
                                    <label>Client Type</label>
                                    <span>{pkg.consigneeType}</span>
                                </div>
                            )}
                            {role !== "client" && (
                                <div className={styles.infoRow}>
                                    <label>Courier Name</label>
                                    {courierNameTemplate(pkg)}
                                </div>
                            )}
                            {role !== "client" && (
                                <div className={styles.infoRow}>
                                    <label>Description</label>
                                    <span>{pkg.description}</span>
                                </div>
                            )}
                            {(role === "admin" || role === "clerk") && (
                                <div className={styles.infoRow}>
                                    <label>Merchant</label>
                                    <span>{pkg.merchant}</span>
                                </div>
                            )}
                            <div className={styles.infoRow}>
                                <label>Tracking Number</label>
                                <span>{pkg.trackingNumber}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <label>Delivery Information</label>
                                <span>{pkg.deliveryInfo}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <label>Created On</label>
                                <span>{pkg.date}</span>
                            </div>
                        </>
                    )}
                </div>

                <button type="button" className={styles.expandToggle} onClick={() => toggleExpand(pkg.Waybill)}>
                    {isExpanded ? "Show less" : "Show more"}
                    <i className={`pi ${isExpanded ? "pi-chevron-up" : "pi-chevron-down"}`} />
                </button>

                <div className={styles.cardFooter}>
                    {role === "admin" || role === "handler" || role === "clerk" || role === "driver" ? (
                        <>
                            <button type="button" className={styles.actionButton} onClick={(e) => handleActionButton(e, pkg)}>
                                <i className="pi pi-ellipsis-v" /> Details
                            </button>
                            <button
                                type="button"
                                className={styles.actionButton}
                                onClick={() => {
                                    setPackageDetail(true);
                                    setPackage(pkg);
                                }}
                            >
                                <i className="pi pi-eye" /> Quick View
                            </button>
                            {pkg.trackingCode && (
                                <button type="button" className={styles.actionButton} onClick={() => history.push(`/track/${pkg.trackingCode}`)}>
                                    <i className="pi pi-map-marker" /> Track
                                </button>
                            )}
                            {role === "admin" && (
                                <button type="button" className={styles.actionButtonDanger} onClick={() => handelDel(pkg)}>
                                    <i className="pi pi-trash" /> Delete
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={styles.actionButton}
                                onClick={() => {
                                    setUploadShow(true);
                                    setPackage(pkg);
                                }}
                            >
                                <i className="pi pi-upload" /> Upload
                            </button>
                            {pkg.trackingCode && (
                                <button type="button" className={styles.actionButton} onClick={() => history.push(`/track/${pkg.trackingCode}`)}>
                                    <i className="pi pi-map-marker" /> Track
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ─── Render ───

    return (
        <>
            <Toast ref={toast} />
            {filtersBackdrop}
            {filtersPanel}
            {filtersFab}
            <div className={styles.tableCard}>
                {header}

                {activeTotal > 0 && <CustomPagination />}

                <div className={styles.sortBar}>
                    <label>Sort by:</label>
                    <select value={sortField} onChange={(e) => setSortField(e.target.value)} className={styles.sortSelect}>
                        <option value="">Default</option>
                        {sortOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    {sortField && (
                        <button type="button" className={styles.sortDirectionBtn} onClick={() => setSortOrder((o) => o * -1)}>
                            <i className={`pi ${sortOrder === 1 ? "pi-sort-amount-up" : "pi-sort-amount-down"}`} />
                        </button>
                    )}
                </div>

                {loading ? <div className={styles.emptyState}>Loading packages...</div> : sortedBrands.length === 0 ? <div className={styles.emptyState}>No packages found.</div> : <div className={styles.cardsGrid}>{sortedBrands.map(renderPackageCard)}</div>}
            </div>
        </>
    );
}

export default PackagesTable;
